import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetBrandV1BrandsBrandIdGetQueryKey,
  getListBrandsV1BrandsGetQueryKey,
  useRenameBrandV1BrandsBrandIdRenamePost,
} from "../api/generated/qcsControlPlane";
import { useCanWrite } from "../auth/me";
import { IconPencil } from "./icons";
import { useToast } from "./toast";
import { Button, Input } from "@akaike/ui";

// The page title, editable in place. The canonical name is not a display label: it is the
// join key into the ClickHouse SKU/event rows and the scope string every job carries, so a
// rename is a propagating server operation (POST /rename) rather than a field write - which
// is why this is a deliberate pencil -> edit -> Save, not a click-to-type heading. The success
// toast reports what actually moved, so the operator sees the blast radius they just caused.
export function BrandNameEditor({ brandId, brand }: { brandId: string; brand: string }) {
  const canWrite = useCanWrite();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(brand);
  // Server rejections (409 name taken, and anything else the endpoint refuses) are held
  // here rather than fired as a toast: the operator is still standing in the editor with
  // the offending name in front of them, so the message belongs next to the input and
  // must survive until they actually change it.
  const [rejected, setRejected] = useState<string | null>(null);
  const qc = useQueryClient();
  const notify = useToast();

  const rename = useRenameBrandV1BrandsBrandIdRenamePost<Error>({
    mutation: {
      onSuccess: (result) => {
        const skus = Object.values(result.clickhouse).reduce((n, c) => n + c.skus, 0);
        notify(
          `Renamed to ${result.brand.brand}: repointed ${skus} SKU${skus === 1 ? "" : "s"}` +
            `, ${result.jobs} job scope${result.jobs === 1 ? "" : "s"}`,
          "ok",
        );
        setEditing(false);
        setRejected(null);
        // Seed the new name from the response before invalidating: closing the editor on
        // an un-refetched cache would flash the OLD name in the heading under a toast
        // announcing the new one, which reads as the rename not having applied.
        qc.setQueryData(getGetBrandV1BrandsBrandIdGetQueryKey(brandId), result.brand);
        qc.invalidateQueries({
          queryKey: getGetBrandV1BrandsBrandIdGetQueryKey(brandId),
        });
        qc.invalidateQueries({ queryKey: getListBrandsV1BrandsGetQueryKey() });
        qc.invalidateQueries({ queryKey: ["sku-counts"] });
      },
      onError: (e) => setRejected(e instanceof Error ? e.message : "rename failed"),
    },
  });

  const open = () => {
    setDraft(brand);
    setRejected(null);
    setEditing(true);
  };
  const close = () => {
    setRejected(null);
    setEditing(false);
  };
  const next = draft.trim();
  // Why Save is off, in the operator's words. A disabled button with no stated reason is
  // the same dead end as a silent failure - especially for "unchanged", where trailing
  // whitespace makes the draft *look* different from the current name.
  const blocked =
    next.length === 0
      ? "Enter a name."
      : next === brand
        ? "That is already this brand's name."
        : null;
  const save = () => {
    if (blocked) return;
    setRejected(null);
    rename.mutate({ brandId, data: { brand: next } });
  };

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-2">
        <span>{brand || brandId}</span>
        {canWrite && brand && (
          <Button
            variant="outline"
            size="sm"
            icon
            onClick={open}
            aria-label="Rename brand"
            title="Rename this brand (repoints its SKUs, history and job scopes)"
          >
            <IconPencil />
          </Button>
        )}
      </span>
    );
  }

  // One message slot, in precedence order: what the server refused, else why Save is off,
  // else what a rename will do. Only the first is an error the operator has to clear.
  const message = rejected ?? blocked ?? null;
  return (
    <span className="inline-flex flex-col gap-1">
      <span className="inline-flex items-center gap-2">
        <Input
          value={draft}
          autoFocus
          disabled={rename.isPending}
          aria-label="Canonical brand name"
          aria-invalid={rejected != null || undefined}
          aria-describedby="rename-brand-message"
          style={{
            fontSize: 18,
            minWidth: 240,
            ...(rejected != null ? { borderColor: "var(--crit-line)" } : null),
          }}
          onChange={(e) => {
            setDraft(e.target.value);
            setRejected(null); // the name they were told about no longer exists
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") close();
          }}
        />
        <Button
          variant="primary"
          size="sm"
          disabled={blocked != null}
          loading={rename.isPending}
          onClick={save}
        >
          Save
        </Button>
        <Button variant="outline" size="sm" disabled={rename.isPending} onClick={close}>
          Cancel
        </Button>
      </span>
      <span
        id="rename-brand-message"
        role={rejected != null ? "alert" : undefined}
        className={rejected != null ? undefined : "faint"}
        style={{
          fontSize: 11.5,
          ...(rejected != null ? { color: "var(--crit)" } : null),
        }}
      >
        {message ??
          "Renaming repoints this brand's SKUs, lifecycle history and job scopes onto the new name."}
      </span>
    </span>
  );
}
